import axiosInstance from "./apiinterseptor";

// Interface for candidate record based on backend model
export interface CandidateRecord {
  _id: string;
  name: string;
  mobile?: string;
  email?: string;
  address?: string;
  city?: string;
  dob?: string;
  gender?: "Male" | "Female" | "Other";
  functionalArea?: string;
  areaOfSpecialization?: string;
  industry?: string;
  resumeTitle?: string;
  keySkills?: string[];
  company?: string;
  salary?: number;
  workExperience?: number;
  level?: string;
  preferredLocation?: string;
  
  courseHighestEducation?: string;
  specializationHighestEducation?: string;
  instituteHighestEducation?: string;
  
  courseSecondHighestEducation?: string;
  specializationSecondHighestEducation?: string;
  instituteSecondHighestEducation?: string;
  
  profileUrl?: string;
  resumeUrl?: string;
  
  phoneViewCount?: number;
  flagged?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Interface for API response
export interface GetCandidateRecordsResponse {
  success: boolean;
  data: {
    candidates: CandidateRecord[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  message: string;
}

// Interface for filter values response
export interface FilterValuesResponse {
  success: boolean;
  data: {
    cities: string[];
    functionalAreas: string[];
    industries: string[];
    levels: string[];
    genders: string[];
    skills?: string[];
  };
  message: string;
}

// Interface for query parameters
export interface GetCandidateRecordsParams {
  page?: number;
  limit?: number;
  search?: string;
  gender?: string | string[];
  city?: string | string[];
  functionalArea?: string | string[];
  industry?: string | string[];
  level?: string | string[];
  skills?: string[]; // Skills is always an array
  minSalary?: number;
  maxSalary?: number;
  minExperience?: number;
  maxExperience?: number;
  minAge?: number;
  maxAge?: number;
}

// Get candidate records with pagination and filtering
export const getCandidateRecords = async (params: GetCandidateRecordsParams = {}): Promise<GetCandidateRecordsResponse> => {
  try {
    const queryParams = new URLSearchParams();
    
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.search) queryParams.append('search', params.search);
    
    // Handle array or string values for filters
    if (params.gender) {
      if (Array.isArray(params.gender)) {
        params.gender.forEach(g => queryParams.append('gender', g));
      } else {
        queryParams.append('gender', params.gender);
      }
    }
    if (params.city) {
      if (Array.isArray(params.city)) {
        params.city.forEach(c => queryParams.append('city', c));
      } else {
        queryParams.append('city', params.city);
      }
    }
    if (params.functionalArea) {
      if (Array.isArray(params.functionalArea)) {
        params.functionalArea.forEach(f => queryParams.append('functionalArea', f));
      } else {
        queryParams.append('functionalArea', params.functionalArea);
      }
    }
    if (params.industry) {
      if (Array.isArray(params.industry)) {
        params.industry.forEach(i => queryParams.append('industry', i));
      } else {
        queryParams.append('industry', params.industry);
      }
    }
    if (params.level) {
      if (Array.isArray(params.level)) {
        params.level.forEach(l => queryParams.append('level', l));
      } else {
        queryParams.append('level', params.level);
      }
    }
    if (params.skills && Array.isArray(params.skills)) {
      params.skills.forEach(s => queryParams.append('skills', s));
    }
    if (params.minSalary !== undefined) {
      queryParams.append('minSalary', params.minSalary.toString());
    }
    if (params.maxSalary !== undefined) {
      queryParams.append('maxSalary', params.maxSalary.toString());
    }
    if (params.minExperience !== undefined) {
      queryParams.append('minExperience', params.minExperience.toString());
    }
    if (params.maxExperience !== undefined) {
      queryParams.append('maxExperience', params.maxExperience.toString());
    }
    if (params.minAge !== undefined) {
      queryParams.append('minAge', params.minAge.toString());
    }
    if (params.maxAge !== undefined) {
      queryParams.append('maxAge', params.maxAge.toString());
    }

    const apiUrl = `/candidateDatabase?${queryParams.toString()}`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Fetching candidate records');
    console.log('Request params:', params);
    console.log('Query params string:', queryParams.toString());
    console.log('API URL:', fullUrl);

    const response = await axiosInstance.get(apiUrl);

    console.log('Candidate records fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching candidate records:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch candidate records. Please try again.');
  }
};

// Get unique filter values
export const getFilterValues = async (): Promise<FilterValuesResponse> => {
  try {
    const apiUrl = '/candidateDatabase/filters';
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Fetching filter values');
    console.log('API URL:', fullUrl);

    const response = await axiosInstance.get(apiUrl);

    console.log('Filter values fetched successfully:', response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching filter values:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch filter values. Please try again.');
  }
};

// Search filter values by type
export const searchFilterValues = async (
  type: 'city' | 'functionalArea' | 'industry' | 'level' | 'skill',
  search?: string,
  limit?: number
): Promise<string[]> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('type', type);
    if (search) queryParams.append('search', search);
    if (limit) queryParams.append('limit', limit.toString());

    const apiUrl = `/candidateDatabase/filters?${queryParams.toString()}`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Searching filter values:', { type, search });
    console.log('API URL:', fullUrl);

    const response = await axiosInstance.get(apiUrl);

    console.log('Filter search results fetched successfully:', response.data);
    return response.data.data; // Return the array of strings
  } catch (error: any) {
    console.error('Error searching filter values:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to search filter values. Please try again.');
  }
};

// Get candidate record by ID
export const getCandidateRecordById = async (id: string): Promise<CandidateRecord> => {
  try {
    const apiUrl = `/candidateDatabase/${id}`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Fetching candidate record by ID:', id);
    console.log('API URL:', fullUrl);

    const response = await axiosInstance.get(apiUrl);

    console.log('Candidate record fetched successfully:', response.data);
    return response.data.data;
  } catch (error: any) {
    console.error('Error fetching candidate record by ID:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch candidate record. Please try again.');
  }
};

// Increment phone view count
export const incrementPhoneViewCount = async (id: string): Promise<number> => {
  try {
    const apiUrl = `/candidateDatabase/${id}/increment-phone-view`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Incrementing phone view count for candidate:', id);

    const response = await axiosInstance.post(apiUrl);

    console.log('Phone view count incremented successfully:', response.data);
    return response.data.data.phoneViewCount || 0;
  } catch (error: any) {
    console.error('Error incrementing phone view count:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to increment phone view count. Please try again.');
  }
};

// Toggle flagged status
export const toggleCandidateFlag = async (id: string): Promise<boolean> => {
  try {
    const apiUrl = `/candidateDatabase/${id}/toggle-flag`;
    const fullUrl = `${axiosInstance.defaults.baseURL}${apiUrl}`;

    console.log('Toggling flag for candidate:', id);

    const response = await axiosInstance.post(apiUrl);

    console.log('Flag toggled successfully:', response.data);
    return response.data.data.flagged || false;
  } catch (error: any) {
    console.error('Error toggling flag:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to toggle flag. Please try again.');
  }
};
